import type { Locale } from "@/lang";

/** Kind of website page, sent with `site-page-view`. */
export type PageType =
  | "home"
  | "article"
  | "category"
  | "resources"
  | "guide"
  | "team"
  | "card"
  | "legal"
  | "other";

const LEGAL_BASES = new Set(["/legal-notice", "/terms-of-sale", "/privacy", "/terms", "/refund"]);

/**
 * Derives the page type from a pathname. English variants (`…/en`) share their French type.
 *
 * @param pathname - URL path without query string.
 * @returns The page type.
 */
export function pageTypeOf(pathname: string): PageType {
  const trimmed = pathname.replace(/\/+$/, "") || "/";
  const base = trimmed === "/en" ? "/" : trimmed.replace(/\/en$/, "") || "/";
  if (base === "/") return "home";
  if (base === "/guide") return "guide";
  if (base === "/team") return "team";
  if (base === "/card") return "card";
  if (LEGAL_BASES.has(base)) return "legal";
  if (base === "/resources") return "resources";
  const parts = base.split("/").filter(Boolean);
  if (parts[0] === "resources" && parts.length === 2) return "category";
  if (parts[0] === "resources" && parts.length === 3) return "article";
  return "other";
}

let currentLocale: Locale | null = null;
let viewPath: string | null = null;
let sentThisView = new Set<string>();

/**
 * Records the language the site is currently rendered in, so events can carry it without
 * every caller passing it. Called by the observability provider on each locale change.
 */
export function setAnalyticsLocale(locale: Locale): void {
  currentLocale = locale;
}

/** Language the site is currently rendered in, or "" before the provider mounted. */
export function getAnalyticsLocale(): string {
  return currentLocale ?? "";
}

/**
 * Starts the page view of `path`: per-page-view deduplication (sections, scroll depth, story
 * steps) starts over. Idempotent for the path already being viewed, so a child component that
 * claims a key before the provider's route effect runs does not get its claim wiped.
 *
 * @param path - Pathname of the page being viewed.
 * @returns true when a new page view started.
 */
export function beginPageView(path: string): boolean {
  if (viewPath === path) return false;
  viewPath = path;
  sentThisView = new Set();
  return true;
}

/**
 * Claims `key` for the current page view (the page at the current URL).
 *
 * @param key - Deduplication key, e.g. `section:pricing`.
 * @returns true the first time `key` is claimed in this page view, false afterwards.
 */
export function claimOncePerPageView(key: string): boolean {
  beginPageView(currentPath());
  if (sentThisView.has(key)) return false;
  sentThisView.add(key);
  return true;
}

/** Current pathname (without query string), or "" on the server. */
export function currentPath(): string {
  return typeof window === "undefined" ? "" : window.location.pathname;
}
